'use client';

import { useEffect, useId, useRef, useState } from 'react';

import { isMusicPreviewClip } from '../functions/isMusicPreviewClip';

/**
 * The stateful `<audio>` island for the music custom button. A music platform's
 * preview clip is capped at 30s; a podcast episode plays to its end
 * ({@link isMusicPreviewClip}). Only one player across the page plays at a time
 * — coordinated via a module-level listener fan-out (other instances pause
 * themselves when the broadcast id changes to something other than their own).
 *
 * Owns the play chip and the card-width seek bar; the thumbnail's dim overlay
 * belongs to the view.
 *
 * Music-specific (lives inside the music variant); the surrounding card is the
 * pure {@link MusicButtonView}. `<audio preload="none">` so the browser doesn't
 * fetch the preview until the user actually presses play — which is also why a
 * full episode's duration is unknown until playback starts.
 */

// ── Module-level coordinator ────────────────────────────────────
//
// Other surfaces subscribe via `subscribeMusicPlayback()` and pause themselves
// when the broadcast owner changes to something other than their own. We only
// need the listener fan-out — there's no consumer of the "current" state
// outside the listener payload.

/**
 * Who is playing, and what. The url is carried so a surface that does not own
 * the audio — the editor's episode picker — can still show the right row as
 * playing.
 */
export interface MusicPlaybackState {
  ownerId: string;
  trackUrl: string | undefined;
}

const listeners = new Set<(state: MusicPlaybackState | undefined) => void>();

export function broadcastMusicPlayback(state: MusicPlaybackState | undefined) {
  for (const listener of listeners) {
    listener(state);
  }
}

export function subscribeMusicPlayback(
  listener: (state: MusicPlaybackState | undefined) => void,
) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * A request for whoever owns `trackUrl` to start or stop it. The editor's
 * episode picker asks rather than playing the selected episode itself, so the
 * card preview stays the one `<audio>` for that track and both surfaces show
 * the same state.
 */
export interface MusicPlaybackRequest {
  trackUrl: string;
  action: 'play' | 'pause';
}

const requestListeners = new Set<(request: MusicPlaybackRequest) => void>();

export function requestMusicPlayback(request: MusicPlaybackRequest) {
  for (const listener of requestListeners) {
    listener(request);
  }
}

export function subscribeMusicPlaybackRequests(
  listener: (request: MusicPlaybackRequest) => void,
) {
  requestListeners.add(listener);
  return () => {
    requestListeners.delete(listener);
  };
}

// ── Icons ───────────────────────────────────────────────────────

function PlayGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      style={{ transform: 'translateX(1px)' }}
    >
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function SpinnerGlyph({ size }: { size: number }) {
  return (
    <svg
      className="animate-spin"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth="3"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PauseGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}

// ── Component ───────────────────────────────────────────────────

interface Props {
  /**
   * Playable audio: a ~30s clip for a music track, the WHOLE episode for a
   * podcast. Missing → nothing renders (exclusive or non-playable episode).
   */
  previewUrl: string | undefined;
  /**
   * Lets another surface showing this track drive this player (the editor's
   * episode picker). Off by default, so a request reaches the card the creator
   * is looking at rather than every copy of the button on the page.
   */
  remoteControlled?: boolean;
  /** Theme CTA color, so the seek bar matches the card's own accent. */
  ctaColor: string;
  /** Thumbnail box the chip centers over — the view owns the card geometry. */
  thumbnailWidth: number;
  rowHeight: number;
}

const PREVIEW_DURATION_MS = 30_000;
const PREVIEW_DURATION_S = 30;
const CHIP_SIZE = 30;
const ICON_SIZE = 14;
const BAR_HEIGHT = 6;
/** Invisible padding above the bar so a thin target is still draggable. */
const BAR_HIT_PADDING = 8;
const DOT_SIZE = 8;
/** Lifts the bar so the dot's overhang isn't clipped by the card's edge. */
const BAR_BOTTOM_OFFSET = 2;

function formatTime(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(whole / 60);
  return `${mins}:${String(whole % 60).padStart(2, '0')}`;
}

export function CustomButtonMusicPlayer({
  previewUrl,
  remoteControlled = false,
  ctaColor,
  thumbnailWidth,
  rowHeight,
}: Props) {
  // Per-instance: the edit modal and the column behind it mount the same
  // button, and a shared key stops the two pausing each other.
  const instanceId = useId();
  const audioRef = useRef<HTMLAudioElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  /** What is loaded, so a resume doesn't reassign `src` (which rewinds). */
  const loadedUrl = useRef<string | undefined>(undefined);
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // A full episode buffers for seconds before `play()` resolves.
  const [isLoading, setIsLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);

  const isClip = isMusicPreviewClip(previewUrl);
  // Prefer the real duration once metadata lands; a clip's 30s cap covers the
  // gap before that. Some feeds report Infinity/NaN — treat those as "unknown"
  // (no fill, no dot, no readout) rather than rendering a nonsense position.
  const measured = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const total = measured || (isClip ? PREVIEW_DURATION_S : 0);
  const hasDuration = total > 0;
  const progress = hasDuration ? Math.min(1, elapsed / total) : 0;

  const clearStopTimer = () => {
    if (stopTimer.current != null) {
      clearTimeout(stopTimer.current);
      stopTimer.current = null;
    }
  };

  /** Stop and rewind — the track is finished with. */
  const reset = () => {
    clearStopTimer();
    setIsPlaying(false);
    setIsLoading(false);
    setElapsed(0);
  };

  /** Stop but keep the position (on the `<audio>` element), so the next tap resumes. */
  const pausePlayback = () => {
    clearStopTimer();
    setIsPlaying(false);
    setIsLoading(false);
  };

  const start = () => {
    const audio = audioRef.current;
    if (!previewUrl || !audio) {
      return;
    }
    // Only load a different track — assigning `src` rewinds.
    if (loadedUrl.current !== previewUrl) {
      audio.src = previewUrl;
      audio.currentTime = 0;
      loadedUrl.current = previewUrl;
    }
    setIsLoading(true);
    audio
      .play()
      .then(() => {
        setIsLoading(false);
        setIsPlaying(true);
        broadcastMusicPlayback({ ownerId: instanceId, trackUrl: previewUrl });
        clearStopTimer();
        if (isClip) {
          // A resumed clip gets what's left of the 30s, not a fresh 30.
          stopTimer.current = setTimeout(
            () => {
              audio.pause();
              audio.currentTime = 0;
              reset();
              broadcastMusicPlayback(undefined);
            },
            Math.max(0, PREVIEW_DURATION_MS - audio.currentTime * 1000),
          );
        }
      })
      .catch(error => {
        setIsLoading(false);
        console.error('Audio play failed:', error);
      });
  };

  const stop = () => {
    audioRef.current?.pause();
    pausePlayback();
    broadcastMusicPlayback(undefined);
  };

  const handleClick = (event: React.MouseEvent) => {
    // The thumbnail sits inside a clickable button-row, so stopping propagation
    // here keeps the play tap from also firing the outer "open music URL"
    // handler.
    event.stopPropagation();
    event.preventDefault();
    if (!previewUrl) {
      return;
    }
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    // A second tap abandons the buffering wait.
    if (isLoading) {
      audio.pause();
      setIsLoading(false);
      return;
    }

    if (!isPlaying) {
      start();
    } else {
      stop();
    }
  };

  const seekTo = (clientX: number) => {
    const audio = audioRef.current;
    const bar = barRef.current;
    if (!audio || !bar) {
      return;
    }
    // The element, not `total`: a stream declaring no length reports `Infinity`
    // well into playback, and a bar that refuses every click reads as broken.
    const length =
      Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : total;
    if (length <= 0) {
      return;
    }
    const { left, width } = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - left) / width));
    audio.currentTime = ratio * length;
    setElapsed(audio.currentTime);
    // The clip cap counts from play-start, so seeking backward would otherwise
    // cut the preview short. Re-arm it for what's left.
    if (isClip && isPlaying) {
      clearStopTimer();
      stopTimer.current = setTimeout(
        () => {
          audio.pause();
          reset();
          broadcastMusicPlayback(undefined);
        },
        Math.max(0, (length - audio.currentTime) * 1000),
      );
    }
  };

  // Same guard as the chip: without it every scrub navigates to the platform.
  const handleBarPointerDown = (event: React.PointerEvent) => {
    event.stopPropagation();
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    seekTo(event.clientX);
  };

  // pointerdown/move guards don't stop the CLICK that follows, which bubbles to
  // the card's anchor and navigates mid-scrub.
  const handleBarClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleBarPointerMove = (event: React.PointerEvent) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }
    event.stopPropagation();
    seekTo(event.clientX);
  };

  // When another music button starts playing, pause this one.
  useEffect(() => {
    return subscribeMusicPlayback(state => {
      // A buffering button must stand down too, or it starts playing later.
      if (state?.ownerId !== instanceId && (isPlaying || isLoading)) {
        audioRef.current?.pause();
        pausePlayback();
      }
    });
  }, [instanceId, isPlaying, isLoading]);

  // A request from another surface showing this track, so one element plays.
  useEffect(() => {
    if (!remoteControlled) return;
    return subscribeMusicPlaybackRequests(request => {
      if (request.trackUrl !== previewUrl) return;
      if (request.action === 'play') {
        if (!isPlaying && !isLoading) start();
      } else if (isPlaying || isLoading) {
        stop();
      }
    });
  }, [remoteControlled, previewUrl, isPlaying, isLoading]);

  // The editor can swap the selected episode mid-play; without this the old
  // audio keeps going under the new selection.
  useEffect(() => {
    if (loadedUrl.current === undefined || loadedUrl.current === previewUrl) {
      return;
    }
    const audio = audioRef.current;
    audio?.pause();
    loadedUrl.current = undefined;
    setDuration(0);
    reset();
    broadcastMusicPlayback(undefined);
  }, [previewUrl]);

  // Always tear down any pending timer on unmount.
  useEffect(
    () => () => {
      clearStopTimer();
    },
    [],
  );

  // Nothing playable → no chip, no bar. The view still dims the thumbnail.
  if (!previewUrl) {
    return null;
  }

  return (
    <>
      {/* On the element, not in a mount effect: this renders `null` whenever the
          episode is briefly url-less (the editor drops `metadata` on every
          edit), which rebuilds the node and leaves such an effect listening to a
          detached one — playback kept working through the ref, position didn't. */}
      <audio
        ref={audioRef}
        preload="none"
        onTimeUpdate={event => setElapsed(event.currentTarget.currentTime)}
        onLoadedMetadata={event => setDuration(event.currentTarget.duration)}
        // A stream with no declared length reports its real one only here.
        onDurationChange={event => setDuration(event.currentTarget.duration)}
        onEnded={() => {
          reset();
          broadcastMusicPlayback(undefined);
        }}
      />

      {/* Play chip over the thumbnail; card-level so the bar spans the width. */}
      <div
        className="absolute left-0 top-0 z-[2] flex items-center justify-center"
        style={{ width: thumbnailWidth, height: rowHeight }}
      >
        <button
          type="button"
          onClick={handleClick}
          aria-label={
            isLoading
              ? 'Loading preview'
              : isPlaying
                ? 'Pause preview'
                : 'Play preview'
          }
          className="flex items-center justify-center rounded-full text-white transition active:scale-95"
          style={{
            width: CHIP_SIZE,
            height: CHIP_SIZE,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          {isLoading ? (
            <SpinnerGlyph size={ICON_SIZE} />
          ) : isPlaying ? (
            <PauseGlyph size={ICON_SIZE} />
          ) : (
            <PlayGlyph size={ICON_SIZE} />
          )}
        </button>
      </div>

      {/* Elapsed / total, whenever the duration is known. */}
      {isPlaying && hasDuration ? (
        <span
          className="absolute z-[2] select-none text-[11px] tabular-nums"
          style={{
            right: 12,
            bottom: BAR_HEIGHT + 6,
            color: ctaColor,
            opacity: 0.85,
          }}
        >
          {formatTime(elapsed)} / {formatTime(total)}
        </span>
      ) : null}

      {/* Seek bar, pinned to the card's bottom edge so it costs no height and
          isn't cramped into the 100px thumbnail. Only while playing — an idle
          card stays clean. */}
      {isPlaying ? (
        <div
          ref={barRef}
          onClick={handleBarClick}
          onPointerDown={handleBarPointerDown}
          onPointerMove={handleBarPointerMove}
          className="absolute left-0 right-0 z-[2]"
          style={{
            bottom: BAR_BOTTOM_OFFSET,
            paddingTop: BAR_HIT_PADDING,
            cursor: 'pointer',
            touchAction: 'none',
          }}
        >
          <div
            className="w-full overflow-hidden"
            style={{
              height: BAR_HEIGHT,
              backgroundColor: `color-mix(in srgb, ${ctaColor} 22%, transparent)`,
            }}
          >
            <div
              className="h-full"
              style={{
                width: hasDuration ? `${progress * 100}%` : '100%',
                backgroundColor: ctaColor,
                // Unknown duration (a live/chunked enclosure) → pulse rather
                // than render a fake position.
                opacity: hasDuration ? 1 : 0.4,
                transition: 'width 200ms linear',
              }}
            />
          </div>
          {/* Playhead — the fill alone reads as static on a long episode. It
              rides the boundary, so the half sitting over the pale track is
              what shows movement. Travel is inset by half the dot so neither
              end clips. */}
          {hasDuration ? (
            <div
              className="pointer-events-none absolute rounded-full"
              style={{
                width: DOT_SIZE,
                height: DOT_SIZE,
                left: `calc(${DOT_SIZE / 2}px + ${progress} * (100% - ${DOT_SIZE}px))`,
                bottom: (BAR_HEIGHT - DOT_SIZE) / 2,
                transform: 'translateX(-50%)',
                backgroundColor: ctaColor,
                transition: 'left 200ms linear',
              }}
            />
          ) : null}
        </div>
      ) : null}
    </>
  );
}
